import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import ImprintTreeWidget from '@/components/ImprintTreeWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL;

const TITLES = {
  impressum: 'Impressum',
  'o-nama': 'O nama',
  'politika-privatnosti': 'Politika privatnosti',
  'postavke-kolacica': 'Postavke kolačića',
  'pravila-komentiranja': 'Pravila komentiranja',
  'pravila-prenosenja-sadrzaja': 'Pravila prenošenja sadržaja',
};

export default function StaticPage({ slug: propSlug }) {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const slug = propSlug || params.slug;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/public/static-pages/${slug}?lang=${i18n.language}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setPage(d))
      .finally(() => setLoading(false));
  }, [slug, i18n.language]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="static-page-loading">
      <Loader2 className="animate-spin text-gray-400" size={28} />
    </div>
  );
  if (!page) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{TITLES[slug] || 'Stranica'}</h1>
      <p className="text-gray-500">{t('notfound.message')}</p>
      <Link to="/" className="inline-block mt-4 text-red-600 hover:underline">{t('notfound.home')}</Link>
    </div>
  );

  // Rewrite /api/uploads links to full backend URL
  const html = (page.body_html || '').replace(/src="\/api\//g, `src="${BACKEND_HOST}/api/`);

  // Imprint team widget can be embedded inline anywhere in the body via the
  // admin "Umetni widget tima" button — that inserts a placeholder div with
  // class "em-imprint-tree". We split the html on that marker and render
  // <ImprintTreeWidget /> in its place. If no marker is present (legacy
  // impressum pages) we auto-append it at the bottom — same as before.
  const MARKER_RE = /<div\b[^>]*\bem-imprint-tree\b[^>]*>[\s\S]*?<\/div>/i;
  const hasMarker = MARKER_RE.test(html);
  const [beforeHtml, afterHtml = ''] = hasMarker ? html.split(MARKER_RE) : [html];

  const proseClasses = "prose prose-lg dark:prose-invert max-w-none [&_.em-columns]:grid [&_.em-columns]:gap-6 [&_.em-columns]:my-6 [&_.em-columns[data-cols='2']]:grid-cols-1 [&_.em-columns[data-cols='2']]:md:grid-cols-2 [&_.em-columns[data-cols='3']]:grid-cols-1 [&_.em-columns[data-cols='3']]:md:grid-cols-3 [&_.em-columns[data-cols='4']]:grid-cols-1 [&_.em-columns[data-cols='4']]:md:grid-cols-4 [&_.em-col]:p-0 [&_.pdf-attachment]:inline-flex [&_.pdf-attachment]:items-center [&_.pdf-attachment]:gap-2 [&_.pdf-attachment]:px-4 [&_.pdf-attachment]:py-2 [&_.pdf-attachment]:bg-red-50 [&_.pdf-attachment]:dark:bg-red-900/20 [&_.pdf-attachment]:text-red-700 [&_.pdf-attachment]:dark:text-red-300 [&_.pdf-attachment]:rounded [&_.pdf-attachment]:no-underline [&_.pdf-attachment]:font-medium [&_figure]:my-6 [&_figure_img]:rounded [&_figcaption]:text-xs [&_figcaption]:text-gray-500 [&_figcaption]:mt-2 [&_figcaption]:text-center";

  return (
    <>
      <Helmet>
        <title>{`${page.title} — e-mina.eu`}</title>
        {page.meta_description && <meta name="description" content={page.meta_description} />}
      </Helmet>
      <div className="max-w-3xl mx-auto px-4 py-10 static-presentational" data-testid={`static-page-${slug}`}>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 dark:text-white mb-6">{page.title}</h1>
        <div className={proseClasses} dangerouslySetInnerHTML={{ __html: beforeHtml }} />
        {hasMarker && <ImprintTreeWidget />}
        {hasMarker && afterHtml && (
          <div className={proseClasses} dangerouslySetInnerHTML={{ __html: afterHtml }} />
        )}
        {!hasMarker && slug === 'impressum' && <ImprintTreeWidget />}
        {page.updated_at && (
          <p className="mt-8 text-xs text-gray-400 dark:text-gray-500">
            {t('common.updated')}: {new Date(page.updated_at).toLocaleDateString(i18n.language)}
          </p>
        )}
      </div>
    </>
  );
}
